/**
 * Error Handler - Standardized error handling for API responses
 * Extracts user-friendly error messages from server responses
 * and provides a consistent way to handle and display errors
 */

/**
 * Extract user-friendly error messages from various error formats
 * Handles Rails API error formats:
 * - { errors: ['message1', 'message2'] }
 * - { error: 'message' }
 * - { message: 'message' }
 * - { status: { message: 'message' } }
 * - Rails model errors object
 * @param {Error} error - Error object from API request
 * @returns {Array<string>} Array of user-friendly error messages
 */
export function extractErrorMessages(error) {
  const messages = []

  // If error has a data property (from apiRequest), check it first
  if (error.data) {
    const data = error.data

    // Handle Rails errors array: { errors: ['message1', 'message2'] }
    if (data.errors) {
      if (Array.isArray(data.errors)) {
        // Array of strings
        messages.push(...data.errors)
      } else if (typeof data.errors === 'object') {
        // Rails model errors object: { field: ['error1', 'error2'] }
        Object.keys(data.errors).forEach(field => {
          const fieldErrors = data.errors[field]
          if (Array.isArray(fieldErrors)) {
            fieldErrors.forEach(err => {
              // Format: "Field name: error message"
              const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
              messages.push(`${fieldName}: ${err}`)
            })
          } else if (typeof fieldErrors === 'string') {
            const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            messages.push(`${fieldName}: ${fieldErrors}`)
          }
        })
      } else if (typeof data.errors === 'string') {
        messages.push(data.errors)
      }
    }

    // Handle single error message: { error: 'message' }
    if (data.error && typeof data.error === 'string') {
      messages.push(data.error)
    }

    // Handle message field: { message: 'message' }
    if (data.message && typeof data.message === 'string') {
      messages.push(data.message)
    }

    // Handle nested status message: { status: { message: 'message' } }
    if (data.status && data.status.message) {
      messages.push(data.status.message)
    }
  }

  // Fall back to error.message if no messages found in data
  if (messages.length === 0 && error.message) {
    // Filter out technical error messages (like HTTP status codes)
    const message = error.message
    if (!message.match(/^HTTP \d+:/) && !message.startsWith('Network error:')) {
      messages.push(message)
    }
  }

  // If still no messages, provide a generic user-friendly message
  if (messages.length === 0) {
    if (error.status === 401) {
      messages.push('You need to sign in to perform this action.')
    } else if (error.status === 403) {
      messages.push('You do not have permission to perform this action.')
    } else if (error.status === 404) {
      messages.push('The requested item could not be found.')
    } else if (error.status === 422) {
      messages.push('The information you provided is invalid. Please check your input and try again.')
    } else if (error.status === 500) {
      messages.push('A server error occurred. Please try again later.')
    } else if (error.status) {
      messages.push('An error occurred. Please try again.')
    } else {
      messages.push('Unable to connect to the server. Please check your internet connection and try again.')
    }
  }

  // Remove duplicates and return
  return [...new Set(messages)]
}

/**
 * Handle API error and trigger error modal
 * This is the main function services should use to handle errors
 * @param {Error} error - Error object from API request
 * @param {Object} options - Optional configuration
 * @param {Function} options.onError - Optional callback function to call before showing modal
 * @param {boolean} options.silent - If true, don't show error modal (default: false)
 * @returns {Object} Error information object with messages and status
 */
export function handleApiError(error, options = {}) {
  const { onError, silent = false } = options

  // Extract user-friendly messages
  const messages = extractErrorMessages(error)

  // Create error info object
  const errorInfo = {
    messages,
    status: error.status || null,
    originalError: error
  }

  // Call optional callback
  if (onError && typeof onError === 'function') {
    onError(errorInfo)
  }

  // Show error modal unless silent mode
  if (!silent) {
    showErrorModal(messages)
  }

  // Log error for debugging (but don't show technical details to users)
  console.error('API Error:', {
    status: error.status,
    messages,
    endpoint: error.endpoint || 'unknown'
  })

  return errorInfo
}

/**
 * Show error modal with messages
 * Uses Vue event system to trigger modal display
 * @param {Array<string>|string} messages - Error message(s) to display
 */
export function showErrorModal(messages) {
  // Convert single message to array
  const messageArray = Array.isArray(messages) ? messages : [messages]

  // Dispatch custom event that App.vue will listen to
  const event = new CustomEvent('show-error-modal', {
    detail: { messages: messageArray }
  })
  window.dispatchEvent(event)
}

/**
 * Wrapper function for API calls that automatically handles errors
 * Use this to wrap API calls in services for consistent error handling
 * @param {Function} apiCall - Async function that makes the API call
 * @param {Object} options - Error handling options
 * @returns {Promise} Promise that resolves with API response or rejects with error info
 */
export async function withErrorHandling(apiCall, options = {}) {
  try {
    return await apiCall()
  } catch (error) {
    const errorInfo = handleApiError(error, options)
    throw errorInfo
  }
}

